import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import Layouts from "../../layouts/layouts";
import "../public/insuranceDetails.css";

const STAGES = [
  "Reported",
  "Survey Pending",
  "Survey Completed",
  "Workshop",
  "Documents Pending",
  "Ready For Submission",
  "Submitted",
  "Approved",
  "Closed",
];

const STATUS_OPTIONS = [
  "Reported",
  "Survey Pending",
  "Survey Completed",
  "Workshop",
  "Documents Pending",
  "Ready For Submission",
  "Submitted",
  "Approved",
  "Rejected",
  "Closed",
];

const AVATAR_COLORS = [
  "linear-gradient(135deg, #6b7280, #3d4457)",
  "linear-gradient(135deg, #6d5dd3, #4c3fa8)",
  "linear-gradient(135deg, #1f9d63, #14713f)",
  "linear-gradient(135deg, #c67c2c, #915617)",
  "linear-gradient(135deg, #d64545, #a52f2f)",
];

const getInitials = (name) =>
  (name ?? "")
    .trim()
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("") || "?";

const getAvatarGradient = (name) => {
  const str = name || "?";
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const statusClass = (status) =>
  "status-" +
  (status || "unknown")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function StatusPill({ status }) {
  return (
    <span className={`status ${statusClass(status)}`}>
      {status || "Unknown"}
    </span>
  );
}

function RemarksDrawer({
  claimId,
  remarks = [],
  open,
  onClose,
  onRemarkAdded,
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [remarks.length, open]);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
      setSending(true);

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claimId}/remark`,
        { message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessage("");
      if (onRemarkAdded) await onRemarkAdded();
    } catch (e) {
      console.error(e);
      alert("Unable to send remark.");
    } finally {
      setSending(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`remarks-overlay ${open ? "open" : ""}`}
        onClick={onClose}
      />

      <div
        className={`remarks-drawer ${open ? "open" : ""}`}
        role="dialog"
        aria-label="Claim conversation"
      >
        <div className="remarks-drawer-header">
          <div>
            <h3>Conversation</h3>
            <p className="drawer-subtitle">
              Claim #{claimId?.slice(-8).toUpperCase()}
            </p>
          </div>
          <button
            className="drawer-close"
            onClick={onClose}
            aria-label="Close conversation"
          >
            ✕
          </button>
        </div>

        <div className="chat-window drawer-chat-window">
          {remarks.length === 0 ? (
            <div className="chat-empty">
              <span>💬</span>
              <p>No remarks yet. Start the conversation below.</p>
            </div>
          ) : (
            <div className="chat-thread">
              {remarks.map((remark) => {
                const isStaff = Boolean(remark.user_id);
                const name =
                  remark.user_id?.name || remark.guest_name || "Guest";

                return (
                  <div
                    className={`chat-row ${isStaff ? "staff" : "guest"}`}
                    key={remark._id}
                  >
                    <div
                      className="chat-avatar"
                      style={{ background: getAvatarGradient(name) }}
                    >
                      {getInitials(name)}
                    </div>

                    <div className="chat-bubble-wrap">
                      <div className="chat-bubble">
                        <div className="chat-bubble-header">
                          <strong>{name}</strong>
                          {isStaff && <span className="chat-badge">Staff</span>}
                        </div>

                        <p className="chat-message">{remark.message}</p>

                        {remark.attachment && (
                          <a
                            href={remark.attachment}
                            target="_blank"
                            rel="noreferrer"
                            className="chat-attachment"
                          >
                            📎 View attachment
                          </a>
                        )}
                      </div>

                      <div className="chat-meta">
                        {new Date(remark.createdAt).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <form className="chat-composer drawer-composer" onSubmit={handleSend}>
          <div className="chat-composer-input">
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder="Type a remark…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />
            <button type="submit" disabled={sending || !message.trim()}>
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

function ClaimTimeline({ status, remarkCount, onOpenRemarks }) {
  const isRejected = status === "Rejected";
  const referenceStatus = isRejected ? "Submitted" : status;
  const currentIndex = STAGES.indexOf(referenceStatus);

  const visibleStages = isRejected ? STAGES.slice(0, currentIndex + 1) : STAGES;

  const progressPct = isRejected
    ? 100
    : currentIndex >= 0
      ? (currentIndex / (STAGES.length - 1)) * 100
      : 0;

  return (
    <div className="section-card">
      <div className="section-header-row">
        <div className="section-title">Claim Progress</div>
        <button className="view-btn" onClick={onOpenRemarks} type="button">
          💬 Conversation{remarkCount > 0 ? ` (${remarkCount})` : ""}
        </button>
      </div>

      <div className="timeline-progress">
        <div className="timeline-progress-bar">
          <div
            className={`timeline-progress-fill ${isRejected ? "is-rejected" : ""}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="timeline-progress-label">
          {isRejected
            ? "Rejected after submission"
            : currentIndex >= 0
              ? `Step ${currentIndex + 1} of ${STAGES.length}`
              : "Status not started"}
        </span>
      </div>

      <div className="insurance-timeline">
        {visibleStages.map((stage, index) => {
          const state =
            isRejected && index === visibleStages.length - 1
              ? "is-done"
              : index < currentIndex
                ? "is-done"
                : index === currentIndex
                  ? "is-active"
                  : "is-upcoming";

          return (
            <div className={`timeline-item ${state}`} key={stage}>
              <div className="timeline-marker">
                {state === "is-done" ? "✓" : index + 1}
              </div>
              <div className="timeline-content">
                <div className={`timeline-title ${state}`}>{stage}</div>
                {state === "is-active" && (
                  <div className="timeline-date">Current Status</div>
                )}
              </div>
            </div>
          );
        })}

        {isRejected && (
          <div className="timeline-item is-rejected">
            <div className="timeline-marker">✕</div>
            <div className="timeline-content">
              <div className="timeline-title is-rejected">Rejected</div>
              <div className="timeline-date is-rejected">Claim Rejected</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InsuranceDocumentModal({
  open,
  document,
  file,
  uploading,
  error,
  newDocumentName,
  setNewDocumentName,
  onClose,
  onFileChange,
  onSubmit,
}) {
  if (!open) return null;

  const isUpdate = Boolean(document);

  return (
    <>
      <div
        className="insurance-document-modal-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget && !uploading) {
            onClose();
          }
        }}
      />

      <div className="insurance-document-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="insurance-document-modal-header">
          <div>
            <h3>{isUpdate ? "Update Document" : "Add Document"}</h3>

            <p>
              {isUpdate
                ? "Replace the existing document with a new file."
                : "Add a new document to this claim."}
            </p>
          </div>

          <button
            type="button"
            className="insurance-document-modal-close"
            onClick={onClose}
            disabled={uploading}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit}>
          <div className="insurance-document-modal-body">
            {/* Error */}
            {error && (
              <div className="insurance-document-modal-error">{error}</div>
            )}

            {/* Document Name */}
            <div className="insurance-document-form-group">
              <label>Document Name</label>

              {isUpdate ? (
                <div className="insurance-document-name-box">
                  📄
                  <span>{document.name}</span>
                </div>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter document name"
                  value={newDocumentName}
                  onChange={(e) => setNewDocumentName(e.target.value)}
                  disabled={uploading}
                />
              )}
            </div>

            {/* Existing File */}
            {document?.file_url && (
              <div className="insurance-document-current">
                <span>Current File</span>

                <a href={document.file_url} target="_blank" rel="noreferrer">
                  View current document
                </a>
              </div>
            )}

            {/* File Upload */}
            <div className="insurance-document-form-group">
              <label>
                {isUpdate ? "Replacement Document" : "Document File"}
              </label>

              <input
                type="file"
                className="form-control"
                onChange={onFileChange}
                disabled={uploading}
              />

              <small>Select the document file you want to upload.</small>
            </div>

            {/* Selected File */}
            {file && (
              <div className="insurance-document-selected">
                <span>Selected File</span>

                <strong>{file.name}</strong>

                <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="insurance-document-modal-footer">
            <button
              type="button"
              className="insurance-document-cancel-btn"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="insurance-document-save-btn"
              disabled={
                !file || uploading || (!isUpdate && !newDocumentName.trim())
              }
            >
              {uploading
                ? "Uploading..."
                : isUpdate
                  ? "Update Document"
                  : "Add Document"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function ClaimSkeleton() {
  return (
    <div className="insurance-page">
      <div className="skeleton skeleton-header" />
      <div className="insurance-layout">
        <div className="insurance-left">
          <div className="insurance-two-col">
            <div className="skeleton skeleton-card" />
            <div className="skeleton skeleton-card" />
          </div>
          <div className="skeleton skeleton-card-tall" />
        </div>
        <div className="insurance-right">
          <div className="skeleton skeleton-card-tall" />
        </div>
      </div>
    </div>
  );
}

function AccidentEditModal({
  open,
  accident,
  accidentForm,
  setAccidentForm,
  saving,
  error,
  vehicleImages,
  vehicleVideos,
  loadImages,
  loadDocuments,
  onClose,
  onChange,
  onVehicleImagesChange,
  onVehicleVideosChange,
  onLoadImagesChange,
  onLoadDocumentsChange,
  onSubmit,
}) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div
        className="insurance-document-modal-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget && !saving) {
            onClose();
          }
        }}
      />

      {/* Modal */}
      <div
        className="insurance-document-modal accident-edit-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="insurance-document-modal-header">
          <div>
            <h3>Edit Accident Details</h3>

            <p>Update accident information or add new supporting files.</p>
          </div>

          <button
            type="button"
            className="insurance-document-modal-close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit}>
          <div className="insurance-document-modal-body">
            {error && (
              <div className="insurance-document-modal-error">{error}</div>
            )}

            {/* ----------------------------------------- */}
            {/* Accident Information */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Accident Information</h4>

              <div className="grid-2">
                {/* Date */}
                <div className="insurance-document-form-group">
                  <label>Accident Date</label>

                  <input
                    type="date"
                    name="accident_date"
                    className="form-control"
                    value={accidentForm.accident_date}
                    onChange={onChange}
                    disabled={saving}
                  />
                </div>

                {/* Time */}
                <div className="insurance-document-form-group">
                  <label>Accident Time</label>

                  <input
                    type="time"
                    name="accident_time"
                    className="form-control"
                    value={accidentForm.accident_time}
                    onChange={onChange}
                    disabled={saving}
                  />
                </div>

                {/* Location */}
                <div
                  className="insurance-document-form-group"
                  style={{ gridColumn: "1 / -1" }}
                >
                  <label>Accident Location</label>

                  <input
                    type="text"
                    name="accident_location"
                    className="form-control"
                    placeholder="Enter accident location"
                    value={accidentForm.accident_location}
                    onChange={onChange}
                    disabled={saving}
                  />
                </div>

                {/* Vehicle Loaded */}
                <div className="insurance-document-form-group">
                  <label>Vehicle Loaded</label>

                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      id="edit_vehicle_loaded"
                      name="vehicle_loaded"
                      className="form-check-input"
                      checked={accidentForm.vehicle_loaded}
                      onChange={onChange}
                      disabled={saving}
                    />

                    <label
                      htmlFor="edit_vehicle_loaded"
                      className="form-check-label"
                    >
                      Vehicle was loaded
                    </label>
                  </div>
                </div>

                {/* Load Details */}
                {accidentForm.vehicle_loaded && (
                  <div className="insurance-document-form-group">
                    <label>Load Details</label>

                    <input
                      type="text"
                      name="load_details"
                      className="form-control"
                      placeholder="Enter load details"
                      value={accidentForm.load_details}
                      onChange={onChange}
                      disabled={saving}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ----------------------------------------- */}
            {/* Vehicle Images */}
            {/* ----------------------------------------- */}

            {/* ----------------------------------------- */}
            {/* Vehicle Images */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Vehicle Images</h4>

              {/* Already uploaded images */}
              {accident?.vehicle_images?.length > 0 && (
                <div className="existing-accident-files">
                  <label className="existing-files-label">
                    Already uploaded
                  </label>

                  <div className="existing-image-grid">
                    {accident.vehicle_images.map((image, index) => (
                      <div
                        key={image._id || image.url || index}
                        className="existing-image-item"
                      >
                        <img
                          src={image.url}
                          alt={image.original_name || `Vehicle ${index + 1}`}
                        />

                        <a
                          href={image.url}
                          target="_blank"
                          rel="noreferrer"
                          className="existing-file-view"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload new images */}
              <input
                type="file"
                className="form-control"
                multiple
                accept="image/*"
                onChange={onVehicleImagesChange}
                disabled={saving}
              />

              {vehicleImages.length > 0 && (
                <small className="text-muted d-block mt-2">
                  {vehicleImages.length} new image(s) selected
                </small>
              )}
            </div>

            {/* ----------------------------------------- */}
            {/* Vehicle Videos */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Vehicle Videos</h4>

              {/* Already uploaded videos */}
              {accident?.vehicle_videos?.length > 0 && (
                <div className="existing-accident-files">
                  <label className="existing-files-label">
                    Already uploaded
                  </label>

                  <div className="existing-video-grid">
                    {accident.vehicle_videos.map((video, index) => (
                      <div
                        key={video._id || video.url || index}
                        className="existing-video-item"
                      >
                        <video src={video.url} controls />

                        <a
                          href={video.url}
                          target="_blank"
                          rel="noreferrer"
                          className="existing-file-view"
                        >
                          View Video
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload new videos */}
              <input
                type="file"
                className="form-control"
                multiple
                accept="video/*"
                onChange={onVehicleVideosChange}
                disabled={saving}
              />

              {vehicleVideos.length > 0 && (
                <small className="text-muted d-block mt-2">
                  {vehicleVideos.length} new video(s) selected
                </small>
              )}
            </div>

            {/* ----------------------------------------- */}
            {/* Load Images */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Load Images</h4>

              {/* Already uploaded images */}
              {accident?.load_images?.length > 0 && (
                <div className="existing-accident-files">
                  <label className="existing-files-label">
                    Already uploaded
                  </label>

                  <div className="existing-image-grid">
                    {accident.load_images.map((image, index) => (
                      <div
                        key={image._id || image.url || index}
                        className="existing-image-item"
                      >
                        <img
                          src={image.url}
                          alt={image.original_name || `Load ${index + 1}`}
                        />

                        <a
                          href={image.url}
                          target="_blank"
                          rel="noreferrer"
                          className="existing-file-view"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload new images */}
              <input
                type="file"
                className="form-control"
                multiple
                accept="image/*"
                onChange={onLoadImagesChange}
                disabled={saving}
              />

              {loadImages.length > 0 && (
                <small className="text-muted d-block mt-2">
                  {loadImages.length} new load image(s) selected
                </small>
              )}
            </div>

            {/* ----------------------------------------- */}
            {/* Load Documents */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Load Documents</h4>

              {/* Already uploaded documents */}
              {accident?.load_documents?.length > 0 && (
                <div className="existing-accident-files">
                  <label className="existing-files-label">
                    Already uploaded
                  </label>

                  <div className="existing-document-list">
                    {accident.load_documents.map((document, index) => (
                      <div
                        key={document._id || document.url || index}
                        className="existing-document-item"
                      >
                        <div className="existing-document-icon">📄</div>

                        <div className="existing-document-info">
                          <strong>
                            {document.original_name ||
                              document.originalName ||
                              document.name ||
                              `Document ${index + 1}`}
                          </strong>

                          {document.mime_type && (
                            <small>{document.mime_type}</small>
                          )}
                        </div>

                        <a
                          href={document.url}
                          target="_blank"
                          rel="noreferrer"
                          className="existing-file-view"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload new documents */}
              <input
                type="file"
                className="form-control"
                multiple
                accept="
      image/*,
      application/pdf,
      application/msword,
      application/vnd.openxmlformats-officedocument.wordprocessingml.document,
      application/vnd.ms-excel,
      application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
    "
                onChange={onLoadDocumentsChange}
                disabled={saving}
              />

              {loadDocuments.length > 0 && (
                <small className="text-muted d-block mt-2">
                  {loadDocuments.length} new document(s) selected
                </small>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="insurance-document-modal-footer">
            <button
              type="button"
              className="insurance-document-cancel-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="insurance-document-save-btn"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Accident Details"}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

function WorkshopCostingModal({
  open,
  workshopForm,
  onChange,
  estimateFiles,
  componentFiles,
  saving,
  error,
  extractingEstimate,
  ocrError,
  onEstimateFilesChange,
  onComponentFilesChange,
  onClose,
  onSubmit,
}) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className="insurance-document-modal-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget && !saving) {
            onClose();
          }
        }}
      />

      <div
        className="insurance-document-modal workshop-cost-edit-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="insurance-document-modal-header">
          <div>
            <h3>Edit Workshop Costing</h3>

            <p>Update repair costing and add workshop supporting documents.</p>
          </div>

          <button
            type="button"
            className="insurance-document-modal-close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit}>
          <div className="insurance-document-modal-body">
            {error && (
              <div className="insurance-document-modal-error">{error}</div>
            )}

            {/* ----------------------------------------- */}
            {/* Costing */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Repair Costing</h4>

              <div className="grid-2">
                <div className="insurance-document-form-group">
                  <label>Insurance Claim Amount</label>

                  <input
                    type="number"
                    name="insurance_claim_amount"
                    className="form-control"
                    placeholder="Enter insurance amount"
                    min="0"
                    step="0.01"
                    value={workshopForm.insurance_claim_amount}
                    onChange={onChange}
                    disabled={saving}
                  />
                </div>

                <div className="insurance-document-form-group">
                  <label>Company Paid Amount</label>

                  <input
                    type="number"
                    name="company_paid_amount"
                    className="form-control"
                    placeholder="Enter company paid amount"
                    min="0"
                    step="0.01"
                    value={workshopForm.company_paid_amount}
                    onChange={onChange}
                    disabled={saving}
                  />
                </div>

                <div className="insurance-document-form-group">
                  <label>Estimate Amount</label>

                  <input
                    type="number"
                    name="estimate_amount"
                    className="form-control"
                    placeholder="Enter estimate amount"
                    min="0"
                    step="0.01"
                    value={workshopForm.estimate_amount}
                    onChange={onChange}
                    disabled={saving}
                  />
                </div>

                <div className="insurance-document-form-group">
                  <label>Total Amount</label>

                  <input
                    type="number"
                    name="total_amount"
                    className="form-control"
                    placeholder="Total amount"
                    min="0"
                    step="0.01"
                    value={workshopForm.total_amount}
                    onChange={onChange}
                    disabled={saving || extractingEstimate}
                  />
                </div>
              </div>
            </div>

            {/* ----------------------------------------- */}
            {/* Payment Summary */}
            {/* ----------------------------------------- */}

            <div className="workshop-edit-summary">
              <div>
                <span>Insurance</span>
                <strong>
                  ₹
                  {Number(
                    workshopForm.insurance_claim_amount || 0,
                  ).toLocaleString("en-IN")}
                </strong>
              </div>

              <div>
                <span>Company</span>
                <strong>
                  ₹
                  {Number(workshopForm.company_paid_amount || 0).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>

              <div>
                <span>Total</span>
                <strong>
                  ₹
                  {Number(workshopForm.total_amount || 0).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>
            </div>

            {/* ----------------------------------------- */}
            {/* Estimate Documents */}
            {/* ----------------------------------------- */}

            {/* ----------------------------------------- */}
            {/* Estimate Documents */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Estimate Documents</h4>

              <p className="section-subtitle">
                Upload the workshop estimate. The system will automatically
                extract the costing using OCR.
              </p>

              <input
                type="file"
                className="form-control"
                multiple
                accept="
      image/*,
      application/pdf,
      application/msword,
      application/vnd.openxmlformats-officedocument.wordprocessingml.document,
      application/vnd.ms-excel,
      application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
    "
                onChange={onEstimateFilesChange}
                disabled={saving || extractingEstimate}
              />

              {/* OCR Loading */}
              {extractingEstimate && (
                <div
                  className="mt-2"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#2563eb",
                    fontSize: "14px",
                  }}
                >
                  <span>⏳</span>
                  <span>Reading estimate and extracting costing...</span>
                </div>
              )}

              {/* OCR Error */}
              {ocrError && !extractingEstimate && (
                <div
                  className="mt-2"
                  style={{
                    color: "#dc2626",
                    fontSize: "14px",
                  }}
                >
                  ⚠️ {ocrError}
                </div>
              )}

              {/* OCR Success */}
              {!extractingEstimate && !ocrError && estimateFiles.length > 0 && (
                <div
                  className="mt-2"
                  style={{
                    color: "#16a34a",
                    fontSize: "14px",
                  }}
                >
                  ✓ Estimate processed successfully
                </div>
              )}

              {estimateFiles.length > 0 && (
                <small className="text-muted d-block mt-2">
                  {estimateFiles.length} new estimate file(s) selected
                </small>
              )}
            </div>

            {/* ----------------------------------------- */}
            {/* Component Documents */}
            {/* ----------------------------------------- */}

            <div className="accident-edit-section">
              <h4>Component Documents</h4>

              <p className="section-subtitle">
                Add component photos or supporting repair files.
              </p>

              <input
                type="file"
                className="form-control"
                multiple
                accept="
                  image/*,
                  application/pdf,
                  application/msword,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  application/vnd.ms-excel,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
                "
                onChange={onComponentFilesChange}
                disabled={saving}
              />

              {componentFiles.length > 0 && (
                <small className="text-muted">
                  {componentFiles.length} new component file(s) selected
                </small>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="insurance-document-modal-footer">
            <button
              type="button"
              className="insurance-document-cancel-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="insurance-document-save-btn"
              disabled={saving || extractingEstimate}
            >
              {extractingEstimate
                ? "Reading Estimate..."
                : saving
                  ? "Saving..."
                  : "Save Workshop Costing"}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

function ChecklistModal({
  open,
  checklistItems,
  tempChecklist,
  checklistSearch,
  setChecklistSearch,
  checklistUpdating,
  error,
  onClose,
  onChange,
  onSubmit,
}) {
  if (!open) return null;

  const filteredChecklistItems = checklistItems.filter((item) =>
    item.defectName?.toLowerCase().includes(checklistSearch.toLowerCase()),
  );

  return ReactDOM.createPortal(
    <>
      <div
        className="insurance-document-modal-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget && !checklistUpdating) {
            onClose();
          }
        }}
      />

      <div className="insurance-document-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="insurance-document-modal-header">
          <div>
            <h3>Update Checklist</h3>

            <p>Select the checklist items for this ticket.</p>
          </div>

          <button
            type="button"
            className="insurance-document-modal-close"
            onClick={onClose}
            disabled={checklistUpdating}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="insurance-document-modal-body">
          {/* Search */}
          <div className="insurance-document-form-group">
            <label>Search Checklist</label>

            <input
              type="text"
              className="form-control"
              placeholder="Search checklist..."
              value={checklistSearch}
              onChange={(e) => setChecklistSearch(e.target.value)}
              disabled={checklistUpdating}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="insurance-document-modal-error">{error}</div>
          )}

          {/* Checklist List */}
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              marginTop: "15px",
              paddingRight: "5px",
            }}
          >
            {filteredChecklistItems.length > 0 ? (
              filteredChecklistItems.map((item) => {
                const isChecked = tempChecklist.includes(item.defectName);

                return (
                  <label
                    key={item._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "10px 12px",
                      marginBottom: "8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      cursor: checklistUpdating ? "not-allowed" : "pointer",
                      backgroundColor: isChecked ? "#f8f9fa" : "#fff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onChange(item.defectName)}
                      disabled={checklistUpdating}
                    />

                    <span>{item.defectName}</span>
                  </label>
                );
              })
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px 10px",
                  color: "#6c757d",
                }}
              >
                No Checklist Found
              </div>
            )}
          </div>

          {/* Selected Count */}
          <div
            style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6c757d",
            }}
          >
            {tempChecklist.length} checklist item
            {tempChecklist.length !== 1 ? "s" : ""} selected
          </div>
        </div>

        {/* Footer */}
        <div className="insurance-document-modal-footer">
          <button
            type="button"
            className="insurance-document-cancel-btn"
            onClick={onClose}
            disabled={checklistUpdating}
          >
            Cancel
          </button>

          <button
            type="button"
            className="insurance-document-save-btn"
            onClick={onSubmit}
            disabled={checklistUpdating}
          >
            {checklistUpdating ? "Updating..." : "Update Checklist"}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function MaintenanceEditModal({
  open,
  maintenanceForm,
  setMaintenanceForm,
  saving,
  error,
  onClose,
  onSubmit,
}) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className="insurance-document-modal-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget && !saving) {
            onClose();
          }
        }}
      />

      <div className="insurance-document-modal" role="dialog" aria-modal="true">
        <div className="insurance-document-modal-header">
          <div>
            <h3>Update Maintenance</h3>
            <p>Update maintenance details for this vehicle service record.</p>
          </div>

          <button
            type="button"
            className="insurance-document-modal-close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="insurance-document-modal-body">
            {error && (
              <div className="insurance-document-modal-error">{error}</div>
            )}

            <div className="grid-2">
              {/* Service Type */}
              {/* Service Type */}
              <div className="insurance-document-form-group">
                <label>Service Type</label>

                <select
                  className="form-control"
                  value={maintenanceForm.serviceType}
                  onChange={(e) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      serviceType: e.target.value,
                    }))
                  }
                  disabled={saving}
                >
                  <option value="">Select Service Type</option>
                  <option value="First">First</option>
                  <option value="Second">Second</option>
                  <option value="Third">Third</option>
                  <option value="Fixed">Fixed</option>
                </select>
              </div>

              {/* Status */}
              <div className="insurance-document-form-group">
                <label>Status</label>

                <select
                  className="form-control"
                  value={maintenanceForm.status}
                  onChange={(e) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  disabled={saving}
                >
                  <option value="">Select Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {/* Last Service Date */}
              <div className="insurance-document-form-group">
                <label>Last Service Date</label>

                <input
                  type="date"
                  className="form-control"
                  value={maintenanceForm.lastServiceDate}
                  onChange={(e) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      lastServiceDate: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              </div>

              {/* Last Service KM */}
              <div className="insurance-document-form-group">
                <label>Last Service KM</label>

                <input
                  type="number"
                  className="form-control"
                  value={maintenanceForm.lastServiceKm}
                  onChange={(e) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      lastServiceKm: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              </div>

              {/* Amount */}
              <div className="insurance-document-form-group">
                <label>Amount</label>

                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  value={maintenanceForm.amount}
                  onChange={(e) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              </div>

              {/* Notes */}
              <div className="insurance-document-form-group">
                <label>Notes</label>

                <textarea
                  className="form-control"
                  rows="3"
                  value={maintenanceForm.notes}
                  onChange={(e) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  disabled={saving}
                />
              </div>
            </div>
          </div>

          <div className="insurance-document-modal-footer">
            <button
              type="button"
              className="insurance-document-cancel-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="insurance-document-save-btn"
              disabled={saving}
            >
              {saving ? "Updating..." : "Update Maintenance"}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

export default function InsuranceClaimView() {
  const { ticket_id } = useParams();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState([]);
  const [tempChecklist, setTempChecklist] = useState([]);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistSearch, setChecklistSearch] = useState("");
  const [checklistUpdating, setChecklistUpdating] = useState(false);
  const [checklistError, setChecklistError] = useState("");
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [newDocumentName, setNewDocumentName] = useState("");

  const [deletingDocumentId, setDeletingDocumentId] = useState(null);

  const [extractingEstimate, setExtractingEstimate] = useState(false);
  const [ocrError, setOcrError] = useState("");

  // ---------------------------------------------------------
  // Workshop Costing Edit Modal
  // ---------------------------------------------------------

  const [workshopModalOpen, setWorkshopModalOpen] = useState(false);
  const [workshopSaving, setWorkshopSaving] = useState(false);
  const [workshopError, setWorkshopError] = useState("");

  const [workshopForm, setWorkshopForm] = useState({
    estimate_amount: "",
    total_amount: "",
    insurance_claim_amount: "",
    company_paid_amount: "",
  });

  const [workshopEstimateFiles, setWorkshopEstimateFiles] = useState([]);
  const [workshopComponentFiles, setWorkshopComponentFiles] = useState([]);

  // ---------------------------------------------------------
  // Accident Edit Modal
  // ---------------------------------------------------------

  const [accidentModalOpen, setAccidentModalOpen] = useState(false);
  const [accidentSaving, setAccidentSaving] = useState(false);
  const [accidentError, setAccidentError] = useState("");

  const [accidentForm, setAccidentForm] = useState({
    accident_date: "",
    accident_time: "",
    accident_location: "",
    vehicle_loaded: false,
    load_details: "",
  });

  const [accidentVehicleImages, setAccidentVehicleImages] = useState([]);
  const [accidentVehicleVideos, setAccidentVehicleVideos] = useState([]);
  const [accidentLoadImages, setAccidentLoadImages] = useState([]);
  const [accidentLoadDocuments, setAccidentLoadDocuments] = useState([]);

  // ---------------------------------------------------------
  // Maintenance Edit Modal
  // ---------------------------------------------------------

  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState("");

  const [maintenanceForm, setMaintenanceForm] = useState({
    serviceType: "",
    status: "",
    lastServiceDate: "",
    lastServiceKm: "",
    amount: "",
    notes: "",
  });

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleCopyPublicLink = async () => {
    if (!claim?.public_token) {
      alert("Public claim link is not available.");
      return;
    }

    try {
      await navigator.clipboard.writeText(claim.public_token);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Unable to copy claim link:", error);
      alert("Unable to copy claim link.");
    }
  };

  const fetchClaim = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/ticket/${ticket_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setClaim({
        ...data.claim,
        maintenance: data.maintenance,
      });

      setSelectedChecklist(
        Array.isArray(data.claim?.ticket_id?.checklist)
          ? data.claim.ticket_id.checklist
          : [],
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("Authentication token not found.");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v3/vehicle-defects`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (Array.isArray(response.data?.defects)) {
        setChecklistItems(response.data.defects);
      } else {
        setChecklistItems([]);
      }
    } catch (error) {
      console.error("Error fetching checklist:", error);
      setChecklistItems([]);
    }
  };

  const handleChecklistChange = (defectName) => {
    setTempChecklist((prev) => {
      if (prev.includes(defectName)) {
        return prev.filter((item) => item !== defectName);
      }

      return [...prev, defectName];
    });
  };

  const handleChecklistUpdate = async () => {
    if (!claim?.ticket_id?._id) {
      setChecklistError("Ticket not found.");
      return;
    }

    try {
      setChecklistUpdating(true);
      setChecklistError("");

      const token = localStorage.getItem("token");

      if (!token) {
        setChecklistError("Authentication token not found.");
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v2/vehicle-ticket/${claim.ticket_id._id}`,
        {
          checklist: tempChecklist,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        setSelectedChecklist([...tempChecklist]);

        await fetchClaim();

        setShowChecklistModal(false);
        setChecklistSearch("");
        setChecklistError("");

        alert("Checklist updated successfully.");
      }
    } catch (error) {
      console.error("Checklist update error:", error.response?.data || error);

      setChecklistError(
        error.response?.data?.message || "Unable to update checklist.",
      );
    } finally {
      setChecklistUpdating(false);
    }
  };

  // ---------------------------------------------------------
  // Update Insurance Claim Status
  // ---------------------------------------------------------

  const handleStatusChange = async (newStatus) => {
    if (!newStatus) return;

    if (!claim?._id) {
      alert("Insurance claim not found.");
      return;
    }

    // Don't call API if status hasn't changed
    if (newStatus === claim.status) {
      return;
    }

    try {
      setStatusUpdating(true);
      setStatusError("");

      const token = localStorage.getItem("token");

      if (!token) {
        setStatusError("Authentication token not found.");
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claim._id}/status`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        // Use returned claim immediately if available
        if (response.data.claim) {
          setClaim(response.data.claim);
        } else {
          await fetchClaim();
        }
      }
    } catch (error) {
      console.error("Status update error:", error);

      setStatusError(
        error.response?.data?.message ||
          "Unable to update insurance claim status.",
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  // ---------------------------------------------------------
  // Open document upload/update modal
  // ---------------------------------------------------------

  const openDocumentModal = (document = null) => {
    setSelectedDocument(document);
    setDocumentFile(null);
    setDocumentError("");
    setDocumentModalOpen(true);
  };

  // ---------------------------------------------------------
  // Close document modal
  // ---------------------------------------------------------

  const closeDocumentModal = () => {
    if (documentUploading) return;

    setDocumentModalOpen(false);
    setSelectedDocument(null);
    setDocumentFile(null);
    setDocumentError("");
  };

  // ---------------------------------------------------------
  // Open Accident Edit Modal
  // ---------------------------------------------------------

  const openAccidentModal = () => {
    const accidentData = claim?.ticket_id?.accident || {};

    setAccidentForm({
      accident_date: accidentData.accident_date
        ? new Date(accidentData.accident_date).toISOString().split("T")[0]
        : "",

      accident_time: accidentData.accident_time || "",

      accident_location: accidentData.accident_location || "",

      vehicle_loaded: Boolean(accidentData.vehicle_loaded),

      load_details: accidentData.load_details || "",
    });

    // Clear newly selected files
    setAccidentVehicleImages([]);
    setAccidentVehicleVideos([]);
    setAccidentLoadImages([]);
    setAccidentLoadDocuments([]);

    setAccidentError("");
    setAccidentModalOpen(true);
  };

  // ---------------------------------------------------------
  // Close Accident Modal
  // ---------------------------------------------------------

  const closeAccidentModal = () => {
    if (accidentSaving) return;

    setAccidentModalOpen(false);
    setAccidentError("");

    setAccidentVehicleImages([]);
    setAccidentVehicleVideos([]);
    setAccidentLoadImages([]);
    setAccidentLoadDocuments([]);
  };

  // ---------------------------------------------------------
  // Open Maintenance Edit Modal
  // ---------------------------------------------------------

  const openMaintenanceModal = () => {
    if (!maintenance?._id) {
      return;
    }

    setMaintenanceForm({
      serviceType: maintenance.serviceType || "",
      status: maintenance.status || "",
      lastServiceDate: maintenance.lastServiceDate
        ? new Date(maintenance.lastServiceDate).toISOString().split("T")[0]
        : "",
      lastServiceKm: maintenance.lastServiceKm ?? "",
      amount: maintenance.amount ?? "",
      notes: maintenance.notes || "",
    });

    setMaintenanceError("");
    setMaintenanceModalOpen(true);
  };

  // ---------------------------------------------------------
  // Close Maintenance Modal
  // ---------------------------------------------------------

  const closeMaintenanceModal = () => {
    if (maintenanceSaving) return;

    setMaintenanceModalOpen(false);
    setMaintenanceError("");
  };

  // ---------------------------------------------------------
  // Accident Form Change
  // ---------------------------------------------------------

  const handleAccidentChange = (event) => {
    const { name, value, type, checked } = event.target;

    setAccidentForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ---------------------------------------------------------
  // Accident File Handlers
  // ---------------------------------------------------------

  const handleAccidentVehicleImages = (event) => {
    setAccidentVehicleImages(Array.from(event.target.files || []));
  };

  const handleAccidentVehicleVideos = (event) => {
    setAccidentVehicleVideos(Array.from(event.target.files || []));
  };

  const handleAccidentLoadImages = (event) => {
    setAccidentLoadImages(Array.from(event.target.files || []));
  };

  const handleAccidentLoadDocuments = (event) => {
    setAccidentLoadDocuments(Array.from(event.target.files || []));
  };
  // ---------------------------------------------------------
  // Select document file
  // ---------------------------------------------------------

  const handleDocumentFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    setDocumentFile(file);
    setDocumentError("");
  };

  // ---------------------------------------------------------
  // Upload / Update Document
  // ---------------------------------------------------------

  const handleDocumentUpload = async (e) => {
    e.preventDefault();

    setDocumentError("");

    // New document needs a name
    if (!selectedDocument && !newDocumentName.trim()) {
      setDocumentError("Please enter a document name.");
      return;
    }

    // File is required
    if (!documentFile) {
      setDocumentError("Please select a document.");
      return;
    }

    // Claim must exist
    if (!claim?._id) {
      setDocumentError("Insurance claim not found.");
      return;
    }

    try {
      setDocumentUploading(true);

      const token = localStorage.getItem("token");

      if (!token) {
        setDocumentError("Authentication token not found.");
        return;
      }

      const formData = new FormData();

      // Existing document -> use its name
      // New document -> use entered name
      const documentName = selectedDocument?.name
        ? selectedDocument.name
        : newDocumentName.trim();

      formData.append("document_name", documentName);

      // IMPORTANT:
      // This must match your multer field name:
      // name: "documents"
      formData.append("documents", documentFile);

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claim._id}/upload-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        // Refresh the claim from backend
        await fetchClaim();

        // Close modal
        closeDocumentModal();
      }
    } catch (error) {
      console.error("Document upload error:", error);

      setDocumentError(
        error.response?.data?.message || "Unable to upload document.",
      );
    } finally {
      setDocumentUploading(false);
    }
  };

  const handleDeleteDocument = async (document) => {
    if (!document?._id) {
      alert("Document ID not found.");
      return;
    }

    if (!claim?._id) {
      alert("Insurance claim not found.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${document.name}"?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingDocumentId(document._id);

      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authentication token not found.");
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claim._id}/document/${document._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        // Refresh complete claim data
        await fetchClaim();

        alert("Document deleted successfully.");
      }
    } catch (error) {
      console.error("Delete document error:", error.response?.data || error);

      alert(error.response?.data?.message || "Unable to delete document.");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  // ---------------------------------------------------------
  // Update Maintenance
  // ---------------------------------------------------------

  const handleMaintenanceSubmit = async (event) => {
    event.preventDefault();

    setMaintenanceError("");

    if (!maintenance?._id) {
      setMaintenanceError("Maintenance record not found.");
      return;
    }

    try {
      setMaintenanceSaving(true);

      const token = localStorage.getItem("token");

      if (!token) {
        setMaintenanceError("Authentication token not found.");
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v4/maintenance-check/${maintenance._id}`,
        {
          serviceType: maintenanceForm.serviceType,
          status: maintenanceForm.status,
          lastServiceDate: maintenanceForm.lastServiceDate,
          lastServiceKm: maintenanceForm.lastServiceKm,
          amount: maintenanceForm.amount,
          notes: maintenanceForm.notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        // ---------------------------------------------
        // If maintenance is completed,
        // ask whether the related ticket should be closed
        // ---------------------------------------------

        if (maintenanceForm.status === "Completed") {
          const closeTicket = window.confirm(
            "Maintenance has been completed.\n\nDo you want to close the ticket?",
          );

          if (closeTicket && claim?._id) {
            try {
              await axios.put(
                `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claim._id}/status`,
                {
                  status: "Closed",
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                },
              );
            } catch (error) {
              console.error("Unable to close insurance claim:", error);

              alert(
                error.response?.data?.message ||
                  "Maintenance completed, but the ticket could not be closed.",
              );
            }
          }
        }

        await fetchClaim();
        closeMaintenanceModal();
      }
    } catch (error) {
      console.error("Maintenance update error:", error);

      setMaintenanceError(
        error.response?.data?.message || "Unable to update maintenance.",
      );
    } finally {
      setMaintenanceSaving(false);
    }
  };

  // ---------------------------------------------------------
  // Update Accident Details
  // ---------------------------------------------------------

  const handleAccidentSubmit = async (event) => {
    event.preventDefault();

    setAccidentError("");

    if (!claim?.ticket_id?._id) {
      setAccidentError("Ticket not found.");
      return;
    }

    try {
      setAccidentSaving(true);

      const token = localStorage.getItem("token");

      if (!token) {
        setAccidentError("Authentication token not found.");
        return;
      }

      const formData = new FormData();

      // -----------------------------------------------------
      // Text fields
      // -----------------------------------------------------

      formData.append("accident_date", accidentForm.accident_date || "");

      formData.append("accident_time", accidentForm.accident_time || "");

      formData.append(
        "accident_location",
        accidentForm.accident_location || "",
      );

      formData.append("vehicle_loaded", String(accidentForm.vehicle_loaded));

      formData.append("load_details", accidentForm.load_details || "");

      // -----------------------------------------------------
      // Vehicle Images
      // -----------------------------------------------------

      accidentVehicleImages.forEach((file) => {
        formData.append("vehicle_images", file);
      });

      // -----------------------------------------------------
      // Vehicle Videos
      // -----------------------------------------------------

      accidentVehicleVideos.forEach((file) => {
        formData.append("vehicle_videos", file);
      });

      // -----------------------------------------------------
      // Load Images
      // -----------------------------------------------------

      accidentLoadImages.forEach((file) => {
        formData.append("load_images", file);
      });

      // -----------------------------------------------------
      // Load Documents
      // -----------------------------------------------------

      accidentLoadDocuments.forEach((file) => {
        formData.append("load_documents", file);
      });

      // -----------------------------------------------------
      // API
      // -----------------------------------------------------

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v2/vehicle-ticket/${claim.ticket_id._id}/accident-details`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        // Refresh claim so the newly uploaded files
        // immediately appear on the page.
        await fetchClaim();

        closeAccidentModal();
      }
    } catch (error) {
      console.error("Accident update error:", error);

      setAccidentError(
        error.response?.data?.message || "Unable to update accident details.",
      );
    } finally {
      setAccidentSaving(false);
    }
  };

  // ---------------------------------------------------------
  // Open Workshop Costing Modal
  // ---------------------------------------------------------

  const openWorkshopModal = () => {
    const workshopData = claim?.workshop || {};
    const amountsData = workshopData.amounts || {};

    setWorkshopForm({
      estimate_amount: workshopData.estimate_amount ?? "",

      total_amount: amountsData.total_amount ?? "",

      insurance_claim_amount: amountsData.insurance_claim_amount ?? "",

      company_paid_amount: amountsData.company_paid_amount ?? "",
    });

    // New files only
    setWorkshopEstimateFiles([]);
    setWorkshopComponentFiles([]);
    setExtractingEstimate(false);
    setOcrError("");

    setWorkshopError("");
    setWorkshopModalOpen(true);
  };

  // ---------------------------------------------------------
  // Close Workshop Costing Modal
  // ---------------------------------------------------------

  const closeWorkshopModal = () => {
    if (workshopSaving || extractingEstimate) return;

    setWorkshopModalOpen(false);
    setWorkshopError("");

    setOcrError("");
    setExtractingEstimate(false);

    setWorkshopEstimateFiles([]);
    setWorkshopComponentFiles([]);
  };

  // ---------------------------------------------------------
  // Workshop Form Change
  // ---------------------------------------------------------

  const handleWorkshopChange = (event) => {
    const { name, value } = event.target;

    setWorkshopForm((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // -----------------------------------------------------
      // Automatically calculate total from payment split
      // -----------------------------------------------------
      if (name === "insurance_claim_amount" || name === "company_paid_amount") {
        updated.total_amount =
          Number(
            name === "insurance_claim_amount"
              ? value
              : prev.insurance_claim_amount || 0,
          ) +
          Number(
            name === "company_paid_amount"
              ? value
              : prev.company_paid_amount || 0,
          );
      }

      return updated;
    });
  };

  // ---------------------------------------------------------
  // Workshop File Handlers
  // ---------------------------------------------------------

  const handleWorkshopEstimateFiles = async (event) => {
    const files = Array.from(event.target.files || []);

    setWorkshopEstimateFiles(files);
    setOcrError("");

    if (!files.length) {
      return;
    }

    // -------------------------------------------------------
    // MAX 5 FILES
    // -------------------------------------------------------

    if (files.length > 5) {
      setOcrError("You can upload a maximum of 5 estimate files.");
      return;
    }

    // -------------------------------------------------------
    // OCR FILE VALIDATION
    // -------------------------------------------------------

    const allowedMimeTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/tiff",
    ];

    for (const file of files) {
      if (!allowedMimeTypes.includes(file.type)) {
        setOcrError(
          `${file.name}: OCR is supported only for PDF, JPG, PNG, or TIFF files.`,
        );
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setOcrError(
          `${file.name}: The estimate file must be smaller than 10 MB.`,
        );
        return;
      }
    }

    try {
      setExtractingEstimate(true);

      const token = localStorage.getItem("token");

      if (!token) {
        setOcrError("Authentication token not found.");
        return;
      }

      // -------------------------------------------------------
      // FORM DATA
      // -------------------------------------------------------

      const formData = new FormData();

      // IMPORTANT:
      // Append EVERY selected file using the SAME field name.
      //
      // Backend:
      // uploadInsurance.array("estimate", 5)
      //
      for (const file of files) {
        formData.append("estimate", file);
      }

      // -------------------------------------------------------
      // DEBUG
      // -------------------------------------------------------

      console.log(
        "Files being uploaded:",
        files.map((file) => file.name),
      );

      // -------------------------------------------------------
      // OCR API
      // -------------------------------------------------------

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claim._id}/workshop-costing/extract-estimate`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // -------------------------------------------------------
      // HANDLE RESPONSE
      // -------------------------------------------------------

      if (response.data?.success) {
        const extracted = response.data.extracted || {};

        console.log("OCR files:", response.data.files);

        console.log("Final combined estimate:", extracted.estimate_amount);

        setWorkshopForm((prev) => ({
          ...prev,

          // Sum of ALL uploaded files
          estimate_amount: extracted.estimate_amount ?? prev.estimate_amount,

          total_amount: extracted.total_amount ?? prev.total_amount,
        }));

        setOcrError("");
      } else {
        setOcrError(
          response.data?.message || "Unable to extract estimate amount.",
        );
      }
    } catch (error) {
      console.error("Estimate OCR error:", error);

      setOcrError(
        error.response?.data?.message || "Unable to read the estimate.",
      );
    } finally {
      setExtractingEstimate(false);
    }
  };

  const handleWorkshopComponentFiles = (event) => {
    setWorkshopComponentFiles(Array.from(event.target.files || []));
  };

  // ---------------------------------------------------------
  // Update Workshop Costing
  // ---------------------------------------------------------

  const handleWorkshopSubmit = async (event) => {
    event.preventDefault();

    setWorkshopError("");

    if (!claim?._id) {
      setWorkshopError("Insurance claim not found.");
      return;
    }

    try {
      setWorkshopSaving(true);

      const token = localStorage.getItem("token");

      if (!token) {
        setWorkshopError("Authentication token not found.");
        return;
      }

      const formData = new FormData();

      // -----------------------------------------
      // Costing
      // -----------------------------------------

      formData.append("estimate_amount", workshopForm.estimate_amount || 0);

      formData.append("total_amount", workshopForm.total_amount || 0);

      formData.append(
        "insurance_claim_amount",
        workshopForm.insurance_claim_amount || 0,
      );

      formData.append(
        "company_paid_amount",
        workshopForm.company_paid_amount || 0,
      );

      // -----------------------------------------
      // Estimate Files
      // -----------------------------------------

      workshopEstimateFiles.forEach((file) => {
        formData.append("estimate_amount_image", file);
      });

      // -----------------------------------------
      // Component Files
      // -----------------------------------------

      workshopComponentFiles.forEach((file) => {
        formData.append("components", file);
      });

      // -----------------------------------------
      // API
      // -----------------------------------------

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/insurance-claim/${claim._id}/workshop-costing`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        await fetchClaim();

        closeWorkshopModal();
      }
    } catch (error) {
      console.error("Workshop costing update error:", error);

      setWorkshopError(
        error.response?.data?.message || "Unable to update workshop costing.",
      );
    } finally {
      setWorkshopSaving(false);
    }
  };

  useEffect(() => {
    fetchClaim();
    fetchChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket_id]);

  if (loading) {
    return (
      <>
        <Layouts />
        <ClaimSkeleton />
      </>
    );
  }

  if (!claim) {
    return (
      <>
        <Layouts />
        <div className="empty-state-page">
          <span>🗂️</span>
          <h2>No claim found</h2>
          <p>This claim may have been removed, or the link is out of date.</p>
        </div>
      </>
    );
  }
  const ticket = claim.ticket_id || {};
  const vehicle = claim.vehicle_id || {};
  const documents = claim.documents || [];
  const comments = ticket.comments || [];
  const accident = ticket.accident || {};
  const workshop = claim.workshop || {};
  const amounts = workshop.amounts || {};
  const maintenance = claim.maintenance || {};
  const oemDetails = vehicle.id_type?.oemDetails || [];
  const ownerDetails = claim?.lease || null;
  console.log("Owner Details:", claim.lease);

  const estimateFiles = workshop.estimate_amount_image || [];
  const componentFiles = workshop.components || [];

  const remarks = claim.remarks || [];
  const uploadedCount = documents.filter((d) => d.uploaded).length;
  const filteredChecklistItems = checklistItems.filter((item) =>
    item.defectName?.toLowerCase().includes(checklistSearch.toLowerCase()),
  );
  return (
    <>
      <Layouts />

      <div className="insurance-page">
        <div className="insurance-header">
          <div>
            <p className="header-eyebrow">Insurance Claim</p>
            <h1>{ticket.heading || "Claim Overview"}</h1>
            <p className="header-claim-id">
              <b>#{claim._id?.slice(-8).toUpperCase()}</b>
              {claim.vehicle_number && (
                <>
                  &nbsp;·&nbsp; Vehicle <b>{claim.vehicle_number}</b>
                </>
              )}
              &nbsp;·&nbsp; Filed{" "}
              <b>{formatDate(ticket.dates?.ticket_issued)}</b>
            </p>
          </div>

          <div className="header-actions">
            <div className="status-update-wrapper">
              <select
                className={`status-select ${statusClass(claim.status)}`}
                value={claim.status || "Reported"}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={statusUpdating}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>

              {statusUpdating && (
                <span className="status-update-loading">Updating...</span>
              )}
            </div>
          </div>
        </div>

        {/* Share Claim Link */}
        <div className="section-card share-claim-card">
          <div className="share-claim-content">
            <div className="share-claim-info">
              <div className="share-claim-icon">🔗</div>

              <div>
                <h3>Share Claim</h3>

                <p>
                  Share this link with the insurance company or service center
                  to provide access to the claim details.
                </p>
              </div>
            </div>

            <button
              type="button"
              className={`share-claim-button ${copied ? "copied" : ""}`}
              onClick={handleCopyPublicLink}
              disabled={!claim.public_token}
            >
              {copied ? "✓ Copied!" : "Copy Link"}
            </button>
          </div>
        </div>

        <div className="insurance-layout">
          <div className="insurance-left">
            <div className="section-card">
              <div className="section-title">Claim Details</div>

              <div className="grid-2">
                <div className="info-item">
                  <label>Status</label>
                  <span>
                    <StatusPill status={claim.status} />
                  </span>
                </div>

                <div className="info-item mono">
                  <label>Issued</label>
                  <span>{formatDate(ticket.dates?.ticket_issued)}</span>
                </div>

                <div className="info-item mono">
                  <label>Tentative Date</label>
                  <span>{formatDate(ticket.dates?.tentative_date)}</span>
                </div>

                <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                  <label>Description</label>
                  <span>{ticket.description || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Survey Required</label>
                  <span>{claim.survey?.survey_required ? "Yes" : "No"}</span>
                </div>

                {/* Checklist */}
                <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                  <div className="checklist-header">
                    <label className="checklist-title">
                      Defects / Checklist
                    </label>

                    <button
                      type="button"
                      className="checklist-update-btn"
                      onClick={() => {
                        setTempChecklist([...selectedChecklist]);
                        setChecklistSearch("");
                        setChecklistError("");
                        setShowChecklistModal(true);
                      }}
                      disabled={checklistUpdating}
                    >
                      ✏️ Update Checklist
                    </button>
                  </div>

                  {selectedChecklist.length > 0 ? (
                    <div className="checklist-chips">
                      {selectedChecklist.map((item, index) => (
                        <div
                          className="checklist-chip"
                          key={`${item}-${index}`}
                        >
                          <span className="checklist-chip-icon">✓</span>

                          <span className="checklist-chip-text">{item}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="checklist-empty">
                      No checklist items selected
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="section-card">
              <div className="section-title">Vehicle Details</div>

              <div className="grid-3">
                <div className="info-item mono">
                  <label>Number Plate</label>
                  <span>{vehicle.numberplate || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Model</label>
                  <span>{vehicle.model || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Company</label>
                  <span>
                    {`${vehicle.id_type.company} - ${vehicle.id_type.vehiclename}` ||
                      "—"}
                  </span>
                </div>

                <div className="info-item mono">
                  <label>Year</label>
                  <span>{vehicle.year || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Driver</label>
                  <span>{claim.driver_id?.name || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Supervisor</label>
                  <span>{claim.supervisor_id?.name || "—"}</span>
                </div>
              </div>
            </div>

            {/* OEM DETAILS */}
            {/* OWNER DETAILS */}
            {ownerDetails && (
              <div className="oem-details-section">
                <div className="section-header-row">
                  <div>
                    <h3>Owner Details</h3>
                  </div>
                </div>

                <div className="oem-table-wrapper">
                  <table className="oem-table">
                    <thead>
                      <tr>
                        <th>Count</th>
                        <th>Owner Name</th>
                        <th>Contact</th>
                        <th>Email</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>1</td>

                        <td>
                          <strong>{ownerDetails.leasing_partner || "—"}</strong>
                        </td>

                        <td>{ownerDetails.phone_number || "—"}</td>

                        <td>{ownerDetails.email_id || "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {oemDetails.length > 0 && (
              <div className="oem-details-section">
                <div className="section-header-row">
                  <div>
                    <h3>OEM Details</h3>
                    <p className="section-subtitle">
                      Original Equipment Manufacturer information
                    </p>
                  </div>

                  <span className="file-count">{oemDetails.length}</span>
                </div>

                <div className="oem-table-wrapper">
                  <table className="oem-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>OEM Name</th>
                        <th>Contact</th>
                        <th>Email</th>
                      </tr>
                    </thead>

                    <tbody>
                      {oemDetails.map((oem, index) => (
                        <tr key={oem._id || index}>
                          <td>{index + 1}</td>

                          <td>
                            <strong>{oem.name || "—"}</strong>
                          </td>

                          <td>{oem.contact || "—"}</td>

                          <td>{oem.email || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Maintenance Details */}
            <div className="insurance-card">
              <div className="section-header-row">
                <div>
                  <h3>Maintenance Details</h3>
                  <p className="section-subtitle">
                    Maintenance record associated with this ticket
                  </p>
                </div>

                {maintenance?._id && (
                  <button
                    type="button"
                    className="view-btn"
                    onClick={openMaintenanceModal}
                  >
                    ✏️ Update Maintenance
                  </button>
                )}
              </div>

              {!maintenance?._id ? (
                <div className="chat-empty">
                  <span>🔧</span>
                  <p>No maintenance record linked to this ticket.</p>
                </div>
              ) : (
                <div className="grid-2">
                  <div className="info-item">
                    <label>Service Type</label>
                    <span>{maintenance.serviceType || "—"}</span>
                  </div>

                  <div className="info-item">
                    <label>Status</label>
                    <span>
                      <StatusPill status={maintenance.status} />
                    </span>
                  </div>

                  <div className="info-item">
                    <label>Last Service Date</label>
                    <span>{formatDate(maintenance.lastServiceDate)}</span>
                  </div>

                  <div className="info-item">
                    <label>Last Service KM</label>
                    <span>{maintenance.lastServiceKm ?? "—"}</span>
                  </div>

                  <div className="info-item">
                    <label>Next Service Due Date</label>
                    <span>{formatDate(maintenance.nextServiceDueDate)}</span>
                  </div>

                  <div className="info-item">
                    <label>Next Service Due KM</label>
                    <span>{maintenance.nextServiceDueKm ?? "—"}</span>
                  </div>

                  <div className="info-item">
                    <label>Amount</label>
                    <span>
                      ₹{Number(maintenance.amount || 0).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                    <label>Notes</label>
                    <span>{maintenance.notes || "—"}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Accident Details */}
            <div className="insurance-card">
              <div className="section-header-row">
                <div>
                  <h3>Accident Details</h3>

                  <p className="section-subtitle">
                    Accident information and supporting documents
                  </p>
                </div>

                <button
                  type="button"
                  className="view-btn"
                  onClick={openAccidentModal}
                >
                  ✏️ Edit Accident
                </button>
              </div>

              <div className="grid-2">
                <div className="info-item">
                  <label>Accident Date</label>
                  <span>{formatDate(accident.accident_date)}</span>
                </div>

                <div className="info-item">
                  <label>Accident Time</label>
                  <span>{accident.accident_time || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Accident Location</label>
                  <span>{accident.accident_location || "—"}</span>
                </div>

                <div className="info-item">
                  <label>Vehicle Loaded</label>
                  <span>{accident.vehicle_loaded ? "Yes" : "No"}</span>
                </div>

                {accident.vehicle_loaded && (
                  <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                    <label>Load Details</label>
                    <span>{accident.load_details || "—"}</span>
                  </div>
                )}
              </div>

              {/* Vehicle Images */}
              {accident.vehicle_images?.length > 0 && (
                <div className="accident-media-section">
                  <h4>Vehicle Images</h4>

                  <div className="accident-media-grid">
                    {accident.vehicle_images.map((image) => (
                      <a
                        key={image._id || image.url}
                        href={image.url}
                        target="_blank"
                        rel="noreferrer"
                        className="accident-media-card"
                      >
                        <img src={image.url} alt="Vehicle accident" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Vehicle Videos */}
              {accident.vehicle_videos?.length > 0 && (
                <div className="accident-media-section">
                  <h4>Vehicle Videos</h4>

                  <div className="accident-media-grid">
                    {accident.vehicle_videos.map((video) => (
                      <div
                        key={video._id || video.url}
                        className="accident-media-card"
                      >
                        <video src={video.url} controls />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Load Images */}
              {accident.load_images?.length > 0 && (
                <div className="accident-media-section">
                  <h4>Load Images</h4>

                  <div className="accident-media-grid">
                    {accident.load_images.map((image) => (
                      <a
                        key={image._id || image.url}
                        href={image.url}
                        target="_blank"
                        rel="noreferrer"
                        className="accident-media-card"
                      >
                        <img src={image.url} alt="Vehicle load" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Load Documents */}
              {accident.load_documents?.length > 0 && (
                <div className="accident-media-section">
                  <h4>Load Documents</h4>

                  <div className="load-document-list">
                    {accident.load_documents.map((document) => (
                      <a
                        key={document._id || document.url}
                        href={document.url}
                        target="_blank"
                        rel="noreferrer"
                        className="load-document-item"
                      >
                        <span className="document-icon">📄</span>

                        <div>
                          <strong>
                            {document.original_name || "Load Document"}
                          </strong>

                          {document.mime_type && (
                            <small>{document.mime_type}</small>
                          )}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {!accident.vehicle_images?.length &&
                !accident.vehicle_videos?.length &&
                !accident.load_images?.length &&
                !accident.load_documents?.length && (
                  <div className="chat-empty">
                    <span>📷</span>
                    <p>No accident attachments available.</p>
                  </div>
                )}
            </div>

            {/* SERVICE CENTER DETAILS */}

            <div className="insurance-card workshop-cost-card">
              <div className="section-header-row">
                <div>
                  <h3>Workshop Costing</h3>

                  <p className="section-subtitle">
                    Repair cost and payment breakdown
                  </p>
                </div>

                <button
                  type="button"
                  className="view-btn"
                  onClick={openWorkshopModal}
                >
                  ✏️ Edit Costing
                </button>
              </div>

              {/* Main Cost Summary */}
              <div className="cost-summary">
                <div className="cost-main-row">
                  <div className="cost-main-item">
                    <span className="cost-label">Estimated Repair Cost</span>

                    <strong className="cost-estimate">
                      ₹
                      {Number(workshop.estimate_amount || 0).toLocaleString(
                        "en-IN",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </strong>
                  </div>

                  <div className="cost-arrow">→</div>

                  <div className="cost-main-item final-cost">
                    <span className="cost-label">Final Repair Cost</span>

                    <strong>
                      ₹
                      {Number(amounts.total_amount || 0).toLocaleString(
                        "en-IN",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </strong>
                  </div>
                </div>

                {/* Difference */}
                {Number(workshop.estimate_amount || 0) !==
                  Number(amounts.total_amount || 0) && (
                  <div className="cost-difference">
                    <span>
                      {Number(amounts.total_amount || 0) <
                      Number(workshop.estimate_amount || 0)
                        ? "Under estimate"
                        : "Over estimate"}
                    </span>

                    <strong>
                      ₹
                      {Math.abs(
                        Number(workshop.estimate_amount || 0) -
                          Number(amounts.total_amount || 0),
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                )}
              </div>

              {/* Payment Breakdown */}
              <div className="payment-breakdown">
                <div className="payment-breakdown-header">
                  <div>
                    <h4>Payment Breakdown</h4>
                    <p>How the final repair cost is being covered</p>
                  </div>

                  <strong className="payment-total">
                    ₹
                    {Number(amounts.total_amount || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </strong>
                </div>

                <div className="payment-cards">
                  {/* Insurance */}
                  <div className="payment-card insurance-payment">
                    <div className="payment-card-top">
                      <div className="payment-icon">🛡️</div>

                      <div>
                        <span className="payment-title">Insurance</span>

                        <span className="payment-description">
                          Insurance claim coverage
                        </span>
                      </div>
                    </div>

                    <div className="payment-amount">
                      ₹
                      {Number(
                        amounts.insurance_claim_amount || 0,
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>

                    <div className="payment-percentage">
                      {amounts.total_amount
                        ? (
                            (Number(amounts.insurance_claim_amount || 0) /
                              Number(amounts.total_amount)) *
                            100
                          ).toFixed(1)
                        : "0.0"}
                      % of total cost
                    </div>

                    <div className="payment-progress">
                      <div
                        className="payment-progress-fill"
                        style={{
                          width: `${
                            amounts.total_amount
                              ? Math.min(
                                  100,
                                  (Number(amounts.insurance_claim_amount || 0) /
                                    Number(amounts.total_amount)) *
                                    100,
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div className="payment-card company-payment">
                    <div className="payment-card-top">
                      <div className="payment-icon">🏢</div>

                      <div>
                        <span className="payment-title">Company</span>

                        <span className="payment-description">
                          Company contribution
                        </span>
                      </div>
                    </div>

                    <div className="payment-amount">
                      ₹
                      {Number(amounts.company_paid_amount || 0).toLocaleString(
                        "en-IN",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </div>

                    <div className="payment-percentage">
                      {amounts.total_amount
                        ? (
                            (Number(amounts.company_paid_amount || 0) /
                              Number(amounts.total_amount)) *
                            100
                          ).toFixed(1)
                        : "0.0"}
                      % of total cost
                    </div>

                    <div className="payment-progress">
                      <div
                        className="payment-progress-fill"
                        style={{
                          width: `${
                            amounts.total_amount
                              ? Math.min(
                                  100,
                                  (Number(amounts.company_paid_amount || 0) /
                                    Number(amounts.total_amount)) *
                                    100,
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Cost Validation */}
                <div className="payment-total-check">
                  <span>Insurance + Company</span>

                  <div>
                    <strong>
                      ₹
                      {(
                        Number(amounts.insurance_claim_amount || 0) +
                        Number(amounts.company_paid_amount || 0)
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>

                    <span className="check-icon">✓</span>
                  </div>
                </div>
              </div>

              {/* Estimate Files */}
              <div className="workshop-files-section">
                <div className="workshop-files-header">
                  <div>
                    <h4>Estimate Documents</h4>
                    <p>Repair estimate uploaded by the workshop</p>
                  </div>

                  <span className="file-count">{estimateFiles.length}</span>
                </div>

                {estimateFiles.length === 0 ? (
                  <div className="chat-empty">
                    <span>📄</span>
                    <p>No estimate files uploaded.</p>
                  </div>
                ) : (
                  <div className="workshop-file-grid">
                    {estimateFiles.map((file) => (
                      <a
                        key={file._id || file.url}
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="workshop-file-card"
                      >
                        {file.mime_type?.startsWith("image/") ? (
                          <img
                            src={file.url}
                            alt={file.original_name || "Estimate"}
                          />
                        ) : (
                          <div className="workshop-file-icon">📄</div>
                        )}

                        <div className="workshop-file-info">
                          <div className="workshop-file-name">
                            {file.original_name || "Estimate File"}
                          </div>

                          <small>{file.mime_type || "File"}</small>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Component Files */}
              <div className="workshop-files-section">
                <div className="workshop-files-header">
                  <div>
                    <h4>Component Documents</h4>
                    <p>Supporting component / repair documentation</p>
                  </div>

                  <span className="file-count">{componentFiles.length}</span>
                </div>

                {componentFiles.length === 0 ? (
                  <div className="chat-empty">
                    <span>🔧</span>
                    <p>No component files uploaded.</p>
                  </div>
                ) : (
                  <div className="workshop-file-grid">
                    {componentFiles.map((file) => (
                      <a
                        key={file._id || file.url}
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="workshop-file-card"
                      >
                        {file.mime_type?.startsWith("image/") ? (
                          <img
                            src={file.url}
                            alt={file.original_name || "Component"}
                          />
                        ) : (
                          <div className="workshop-file-icon">🔧</div>
                        )}

                        <div className="workshop-file-info">
                          <div className="workshop-file-name">
                            {file.original_name || "Component File"}
                          </div>

                          <small>{file.mime_type || "File"}</small>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="insurance-card">
              <div className="section-header-row">
                <div>
                  <h3>Documents</h3>

                  {documents.length > 0 && (
                    <span className="doc-progress">
                      {uploadedCount} of {documents.length} uploaded
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="view-btn"
                  onClick={() => openDocumentModal()}
                >
                  + Add Document
                </button>
              </div>

              {documents.length === 0 ? (
                <div className="chat-empty">
                  <span>📄</span>
                  <p>No documents required for this claim.</p>
                </div>
              ) : (
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Required</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc._id}>
                        <td>
                          <span className="doc-name">{doc.name}</span>
                        </td>
                        <td>{doc.required ? "Yes" : "No"}</td>
                        <td>
                          {doc.uploaded ? (
                            <span className="uploaded">Uploaded</span>
                          ) : (
                            <span className="pending">Pending</span>
                          )}
                        </td>
                        <td>
                          <div className="document-actions">
                            {/* View */}
                            {doc.file_url && (
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="view-btn"
                              >
                                View
                              </a>
                            )}

                            {/* Upload / Update */}
                            <button
                              type="button"
                              className="document-edit-btn"
                              onClick={() => openDocumentModal(doc)}
                            >
                              {doc.uploaded ? "Update" : "Upload"}
                            </button>

                            {/* Delete only uploaded manual documents */}
                            {doc.uploaded && (
                              <button
                                type="button"
                                className="document-delete-btn"
                                onClick={() => handleDeleteDocument(doc)}
                                disabled={deletingDocumentId === doc._id}
                              >
                                {deletingDocumentId === doc._id
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="insurance-right">
            <ClaimTimeline
              status={claim.status}
              remarkCount={remarks.length}
              onOpenRemarks={() => setRemarksOpen(true)}
            />

            {/* <div className="insurance-card" style={{ marginTop: 24 }}>
              <h3>Comments</h3>

              {comments.length === 0 ? (
                <div className="chat-empty">
                  <span>💬</span>
                  <p>No comments yet.</p>
                </div>
              ) : (
                <div className="comment-list">
                  {comments.map((c) => (
                    <div key={c._id} className="comment-item">
                      <p className="comment-text">{c.text}</p>
                      <span className="comment-date">{formatDate(c.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>

      <button
        className="remarks-fab"
        onClick={() => setRemarksOpen(true)}
        aria-label="Open conversation"
        type="button"
      >
        💬
        {remarks.length > 0 && (
          <span className="remarks-fab-badge">{remarks.length}</span>
        )}
      </button>

      <RemarksDrawer
        claimId={claim._id}
        remarks={remarks}
        open={remarksOpen}
        onClose={() => setRemarksOpen(false)}
        onRemarkAdded={fetchClaim}
      />

      <ChecklistModal
        open={showChecklistModal}
        checklistItems={checklistItems}
        tempChecklist={tempChecklist}
        checklistSearch={checklistSearch}
        setChecklistSearch={setChecklistSearch}
        checklistUpdating={checklistUpdating}
        error={checklistError}
        onClose={() => {
          if (!checklistUpdating) {
            setShowChecklistModal(false);
            setChecklistSearch("");
            setChecklistError("");
          }
        }}
        onChange={handleChecklistChange}
        onSubmit={handleChecklistUpdate}
      />

      <InsuranceDocumentModal
        open={documentModalOpen}
        document={selectedDocument}
        file={documentFile}
        uploading={documentUploading}
        error={documentError}
        newDocumentName={newDocumentName}
        setNewDocumentName={setNewDocumentName}
        onClose={closeDocumentModal}
        onFileChange={handleDocumentFileChange}
        onSubmit={handleDocumentUpload}
      />

      <AccidentEditModal
        open={accidentModalOpen}
        accident={accident}
        accidentForm={accidentForm}
        setAccidentForm={setAccidentForm}
        saving={accidentSaving}
        error={accidentError}
        vehicleImages={accidentVehicleImages}
        vehicleVideos={accidentVehicleVideos}
        loadImages={accidentLoadImages}
        loadDocuments={accidentLoadDocuments}
        onClose={closeAccidentModal}
        onChange={handleAccidentChange}
        onVehicleImagesChange={handleAccidentVehicleImages}
        onVehicleVideosChange={handleAccidentVehicleVideos}
        onLoadImagesChange={handleAccidentLoadImages}
        onLoadDocumentsChange={handleAccidentLoadDocuments}
        onSubmit={handleAccidentSubmit}
      />

      <WorkshopCostingModal
        open={workshopModalOpen}
        workshopForm={workshopForm}
        onChange={handleWorkshopChange}
        estimateFiles={workshopEstimateFiles}
        componentFiles={workshopComponentFiles}
        saving={workshopSaving}
        error={workshopError}
        extractingEstimate={extractingEstimate}
        ocrError={ocrError}
        onEstimateFilesChange={handleWorkshopEstimateFiles}
        onComponentFilesChange={handleWorkshopComponentFiles}
        onClose={closeWorkshopModal}
        onSubmit={handleWorkshopSubmit}
      />

      <MaintenanceEditModal
        open={maintenanceModalOpen}
        maintenanceForm={maintenanceForm}
        setMaintenanceForm={setMaintenanceForm}
        saving={maintenanceSaving}
        error={maintenanceError}
        onClose={closeMaintenanceModal}
        onSubmit={handleMaintenanceSubmit}
      />
    </>
  );
}
